// RuleStore.ts
import { BaseStore } from './base.store';
import { RootStore } from './index';
import { computed, makeObservable } from 'mobx';
import { RuleHelper } from '../../utils/rule-helper';
import { FwVersion, Rule, Service } from '../../dtos/version.dto';
import { SystemConfiguration } from '../../utils/system-configuration';

export class RuleStore extends BaseStore {
    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            currentVersion: computed,
            MyNetworkToInternetAllowedApplications: computed,
            MyNetworkToInternetBlockedApplications: computed,
            MyNetworkToInternetBlockedServices: computed,
            InternetToMyNetworkAllowedApplications: computed,
            InternetToMyNetworkBlockedApplications: computed,
            InternetToMyNetworkBlockedServices: computed,
            GeoIpDest: computed,
            GeoIpSrc: computed,
            AllowUntrustDest: computed,
            AllowUntrustSrc: computed,
            BlockUntrustDest: computed,
            BlockUntrustSrc: computed,
            DropAttBlockListDest: computed,
            DropAttBlockListSrc: computed,
            DropAllRule: computed,
            isAppsInboundAllowAll: computed,
            isAppsOutboundAllowAll: computed,
            defaultServicesWithStatus: computed,
            customServices: computed,
            customServicesWithStatus: computed,
            specialServices: computed,
            customServicesWithoutSpecialNames: computed
        });
    }

    get currentVersion() {
        return this.rootStore.fwVersionStore.selectedFwVersion;
    }

    getRule(getRuleMethod: (version: FwVersion) => Rule) {
        if (!this.currentVersion) return null;
        return getRuleMethod(this.currentVersion);
    }

    get MyNetworkToInternetAllowedApplications() {
        return this.getRule(RuleHelper.getMyNetworkToInternetAllowedApplications);
    }

    get MyNetworkToInternetBlockedApplications() {
        return this.getRule(RuleHelper.getMyNetworkToInternetBlockedApplications);
    }

    get MyNetworkToInternetBlockedServices() {
        return this.getRule(RuleHelper.getMyNetworkToInternetBlockedServices);
    }

    get InternetToMyNetworkAllowedServices() {
        return this.getRule(RuleHelper.getInternetToMyNetworkAllowedServices);
    }

    get InternetToMyNetworkAllowedApplications() {
        return this.getRule(RuleHelper.getInternetToMyNetworkAllowedApplications);
    }

    get InternetToMyNetworkBlockedApplications() {
        return this.getRule(RuleHelper.getInternetToMyNetworkBlockedApplications);
    }

    get InternetToMyNetworkBlockedServices() {
        return this.getRule(RuleHelper.getInternetToMyNetworkBlockedServices);
    }

    get AllowMyNetworkToInternetNonWebServices() {
        return this.getRule(RuleHelper.getRuleAllowMyNetworkToInternetNonWebServices);
    }

    get AllowInternetToMyNetworkNonWebServices() {
        return this.getRule(RuleHelper.getRuleAllowInternetToMyNetworkNonWebServices);
    }

    get MyNetworkToInternetServices() {
        return this.getRule(RuleHelper.getRuleMyNetworkToInternetServices);
    }

    get GeoIpDest() {
        return this.getRule(RuleHelper.getRuleGeoIpDest);
    }

    get GeoIpSrc() {
        return this.getRule(RuleHelper.getRuleGeoIpSrc);
    }

    get AllowUntrustDest() {
        return this.getRule(RuleHelper.getRuleAllowUntrustDest);
    }

    get AllowUntrustSrc() {
        return this.getRule(RuleHelper.getRuleAllowUntrustSrc);
    }

    get BlockUntrustDest() {
        return this.getRule(RuleHelper.getRuleBlockUntrustDest);
    }

    get BlockUntrustSrc() {
        return this.getRule(RuleHelper.getRuleBlockUntrustSrc);
    }

    get DropAttBlockListDest() {
        return this.getRule(RuleHelper.getDropAttBlockListDest);
    }

    get DropAttBlockListSrc() {
        return this.getRule(RuleHelper.getDropAttBlockListSrc);
    }

    get DropAllRule() {
        return this.getRule(RuleHelper.getRuleDropAll);
    }

    get isAppsInboundAllowAll() {
        return !!this.InternetToMyNetworkAllowedApplications &&
            RuleHelper.isAllowedByDefault(this.InternetToMyNetworkAllowedApplications);
    }

    get isAppsOutboundAllowAll() {
        return !!this.MyNetworkToInternetAllowedApplications &&
            RuleHelper.isAllowedByDefault(this.MyNetworkToInternetAllowedApplications);
    }

    get defaultServicesWithStatus() {
        return RuleHelper.getDefaultServicesWithStatus(SystemConfiguration.getInstance().defaultServiceDtoList, this.currentVersion);
    }

    get customServicesWithStatus() {
        return RuleHelper.getCustomServicesWithStatus(this.customServices, this.currentVersion);
    }

    get customServices(): Service[] {
        const { fwVersionStore } = this.rootStore;
        const { AllowInternetToMyNetworkNonWebServices, AllowMyNetworkToInternetNonWebServices,
            MyNetworkToInternetBlockedServices, InternetToMyNetworkBlockedServices} = this;

        if (!fwVersionStore.selectedFwVersion?.content?.policy.services ||
            !AllowMyNetworkToInternetNonWebServices || !AllowInternetToMyNetworkNonWebServices
             || !MyNetworkToInternetBlockedServices || !InternetToMyNetworkBlockedServices) {
            return [];
        }

        const customServices = fwVersionStore.selectedFwVersion.content.policy.services;
        customServices.forEach(service => {
            service.serviceStateOutbound = RuleHelper.getServiceState(AllowMyNetworkToInternetNonWebServices, MyNetworkToInternetBlockedServices, service.id);
            service.serviceStateInbound = RuleHelper.getServiceState(AllowInternetToMyNetworkNonWebServices, InternetToMyNetworkBlockedServices, service.id);
            service.isDefault =  false;
        });
        return customServices;
    }

    get specialServices() {
        return SystemConfiguration.getInstance().specialCustomServicesNamesDtoList;
    }

    get customServicesWithoutSpecialNames() {
        return this.customServices.filter(_ => !this.specialServices.includes(_.displayName));
    }

}